import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  IconButton,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  IconChevronLeft,
  IconChevronRight,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandFacebook,
  IconX,
  IconExternalLink,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import Button from "../components/Button";
import { fetchGet } from "../util/helpers";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const platformIcon = {
  facebook: <IconBrandFacebook size={12} />,
  instagram: <IconBrandInstagram size={12} />,
  tiktok: <IconBrandTiktok size={12} />,
  youtube: <IconBrandYoutube size={12} />,
  linkedin: <IconBrandLinkedin size={12} />,
  twitter: <IconBrandTwitter size={12} />,
};

const platformColor = {
  facebook: "#1877F2",
  instagram: "#C13584",
  tiktok: "#000",
  youtube: "#FF0000",
  linkedin: "#0A66C2",
  twitter: "#1DA1F2",
};

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#9E9E9E" },
  queued: { label: "Queued", color: "#2196F3" },
  pending_approval: { label: "Pending", color: "#FF9800" },
  published: { label: "Published", color: "#4CAF50" },
  boosted: { label: "Boosted", color: "#9C27B0" },
};

const fadeIn = {
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export default function SocialCalendarPage() {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    fetchGet("/team/channels").then((res) => {
      if (res?.result) {
        setAvailableChannels(res.result);
        setSelectedChannels(res.result.map((ch) => ch.name || ch.id));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const channels = selectedChannels.join(",");
    const start = currentMonth.startOf("month").toISOString();
    const end = currentMonth.endOf("month").toISOString();
    fetchGet(`/social/posts/calendar?channels=${channels}&startDate=${start}&endDate=${end}`)
      .then((res) => { if (res?.result) setPosts(res.result); })
      .catch(() => {});
  }, [currentMonth, selectedChannels]);

  const daysInMonth = currentMonth.daysInMonth();
  const firstDayOfWeek = currentMonth.startOf("month").day();
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getPostsForDay = (day) => {
    if (!day) return [];
    const dateStr = currentMonth.date(day).format("YYYY-MM-DD");
    return posts.filter((p) => {
      const postDate = p.scheduledAt || (p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000).toISOString() : p.createdAt);
      return postDate && dayjs(postDate).format("YYYY-MM-DD") === dateStr;
    });
  };

  const isToday = (day) => day && dayjs().date() === day && currentMonth.month() === dayjs().month() && currentMonth.year() === dayjs().year();

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", bgcolor: "#FAFBFC", ...fadeIn }}>
      {/* ── Gradient Header Banner ── */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 50%, #C084FC 100%)",
          borderRadius: 4,
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          mb: 3,
          boxShadow: "0 8px 32px rgba(124, 58, 237, 0.18)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -20,
            left: "30%",
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, letterSpacing: "-0.02em" }}>
            Social Calendar
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5, fontSize: 14 }}>
            Plan, schedule, and visualize your content pipeline
          </Typography>
        </Box>
      </Paper>

      {/* ── Channel Filter (frosted glass) ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
          Channels
        </Typography>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Select Channels</InputLabel>
          <Select
            multiple
            value={selectedChannels}
            onChange={(e) => setSelectedChannels(e.target.value)}
            input={<OutlinedInput label="Select Channels" />}
            sx={{ borderRadius: 2.5, bgcolor: "#fff" }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((val) => (
                  <Chip
                    key={val}
                    label={val}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      background: "linear-gradient(135deg, #F3F0FF, #EDE9FE)",
                      color: "#7C3AED",
                      borderRadius: 1.5,
                    }}
                  />
                ))}
              </Box>
            )}
          >
            {availableChannels.map((ch) => (
              <MenuItem key={ch.id} value={ch.name || ch.id}>
                <Checkbox checked={selectedChannels.includes(ch.name || ch.id)} />
                <ListItemText primary={ch.name || ch.platform} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* ── Calendar ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          animation: "fadeInUp 0.4s ease-out both",
        }}
      >
        {/* Month navigation bar — gradient accent */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 2,
            gap: 3,
            background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
          }}
        >
          <IconButton
            onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              width: 36,
              height: 36,
            }}
          >
            <IconChevronLeft size={20} />
          </IconButton>
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, minWidth: 200, textAlign: "center" }}>
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <IconButton
            onClick={() => setCurrentMonth((m) => m.add(1, "month"))}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              width: 36,
              height: 36,
            }}
          >
            <IconChevronRight size={20} />
          </IconButton>
        </Box>

        {/* Weekday headers */}
        <Grid container columns={7}>
          {WEEKDAYS.map((day) => (
            <Grid
              item xs={1} key={day}
              sx={{
                p: 1.5,
                textAlign: "center",
                bgcolor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                borderRight: "1px solid #F3F4F6",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
                {day}
              </Typography>
            </Grid>
          ))}

          {/* Calendar cells */}
          {cells.map((day, i) => {
            const dayPosts = getPostsForDay(day);
            const today = isToday(day);
            return (
              <Grid
                item xs={1} key={i}
                sx={{
                  minHeight: 120,
                  p: 1,
                  borderRight: (i + 1) % 7 !== 0 ? "1px solid #F3F4F6" : "none",
                  borderBottom: "1px solid #F3F4F6",
                  bgcolor: today ? "rgba(124, 58, 237, 0.03)" : "#fff",
                  transition: "background 0.2s, box-shadow 0.2s",
                  cursor: day ? "default" : undefined,
                  "&:hover": day ? {
                    bgcolor: today ? "rgba(124, 58, 237, 0.06)" : "#FAFBFC",
                    boxShadow: "inset 0 0 0 1px rgba(124, 58, 237, 0.12)",
                  } : {},
                }}
              >
                {day && (
                  <>
                    {/* Day number — today gets a purple circle */}
                    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          ...(today
                            ? {
                                background: "linear-gradient(135deg, #7C3AED, #A855F7)",
                                color: "#fff",
                                fontWeight: 800,
                                fontSize: 13,
                                boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)",
                              }
                            : {
                                fontWeight: 500,
                                fontSize: 13,
                                color: "#374151",
                              }),
                        }}
                      >
                        {day}
                      </Box>
                    </Box>

                    {/* Post pills */}
                    {dayPosts.slice(0, 3).map((p) => {
                      const statusCfg = STATUS_LABELS[p.status] || STATUS_LABELS.draft;
                      return (
                        <Box
                          key={p.id}
                          sx={{
                            mb: 0.5,
                            p: "4px 6px",
                            borderRadius: 1.5,
                            bgcolor: statusCfg.color + "12",
                            borderLeft: `3px solid ${statusCfg.color}`,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            "&:hover": {
                              bgcolor: statusCfg.color + "22",
                              transform: "translateX(2px)",
                            },
                          }}
                          onClick={() => setSelectedPost(p)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mb: "1px" }}>
                            {p.platforms?.slice(0, 2).map((pl) => (
                              <Box key={pl} sx={{ display: "flex", color: platformColor[pl] }}>{platformIcon[pl]}</Box>
                            ))}
                          </Box>
                          <Typography noWrap sx={{ fontSize: 10, lineHeight: 1.3, fontWeight: 500, color: "#374151" }}>
                            {p.caption?.substring(0, 20) || "Untitled"}
                          </Typography>
                        </Box>
                      );
                    })}
                    {dayPosts.length > 3 && (
                      <Typography sx={{ color: "#7C3AED", fontSize: 10, cursor: "pointer", fontWeight: 600, mt: 0.25, "&:hover": { textDecoration: "underline" } }}>
                        +{dayPosts.length - 3} more
                      </Typography>
                    )}
                  </>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* ── Status Legend ── */}
      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          px: 3,
          py: 1.5,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, mr: 1 }}>
          Status
        </Typography>
        {Object.entries(STATUS_LABELS).map(([key, cfg]) => (
          <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: cfg.color }} />
            <Typography variant="caption" sx={{ fontSize: 12, color: "#6B7280" }}>{cfg.label}</Typography>
          </Box>
        ))}
      </Paper>

      {/* ── Post Detail Dialog ── */}
      <Dialog
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {selectedPost && (
          <>
            {/* Gradient dialog header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
                px: 3,
                py: 2.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>Post Details</Typography>
              <IconButton
                onClick={() => setSelectedPost(null)}
                size="small"
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.15)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                }}
              >
                <IconX size={18} />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", gap: 0.75, mb: 2.5, flexWrap: "wrap" }}>
                {selectedPost.platforms?.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    size="small"
                    icon={platformIcon[p]}
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      background: `${platformColor[p]}12`,
                      color: platformColor[p],
                      border: `1px solid ${platformColor[p]}30`,
                      borderRadius: 2,
                    }}
                  />
                ))}
                <Chip
                  label={STATUS_LABELS[selectedPost.status]?.label || selectedPost.status}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    background: `linear-gradient(135deg, ${(STATUS_LABELS[selectedPost.status]?.color || "#999")}18, ${(STATUS_LABELS[selectedPost.status]?.color || "#999")}30)`,
                    color: STATUS_LABELS[selectedPost.status]?.color,
                    borderRadius: 2,
                  }}
                />
              </Box>

              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2.5, lineHeight: 1.7, color: "#374151" }}>
                {selectedPost.caption}
              </Typography>

              {selectedPost.media?.length > 0 && (
                <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                  {selectedPost.media.map((m, i) => (
                    <Grid item xs={6} key={i}>
                      <Box
                        component="img"
                        src={m.url}
                        alt={m.fileName}
                        sx={{
                          width: "100%",
                          borderRadius: 2,
                          objectFit: "cover",
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {selectedPost.scheduledAt && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#F9FAFB",
                    border: "1px solid rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, fontSize: 13 }}>
                    Scheduled:
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#7C3AED", fontWeight: 600, fontSize: 13 }}>
                    {dayjs(selectedPost.scheduledAt).format("MMM D, YYYY h:mm A")}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                <Button
                  gradient
                  style={{
                    borderRadius: 10,
                    padding: "10px 28px",
                    fontSize: 14,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={() => {
                    setSelectedPost(null);
                    navigate(`/social/posts/${selectedPost.id}`);
                  }}
                >
                  <IconExternalLink size={16} />
                  View Full Details
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
